/**
 * Card component displaying a single box in the grid.
 */

import type { Box } from '@shared/types';

interface BoxCardProps {
  box: Box;
  onClick: (id: string) => void;
}

export function BoxCard({ box, onClick }: BoxCardProps) {
  return (
    <button
      type="button"
      onClick={() => onClick(box.id)}
      className="w-full rounded-xl border border-border bg-surface p-4 text-left hover:border-accent/50 transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background"
      data-testid={`box-card-${box.id}`}
    >
      {/* Header: name + marker badge */}
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-semibold text-text-primary truncate">{box.name}</h3>
        <span className="shrink-0 rounded bg-accent/10 px-1.5 py-0.5 text-[10px] font-medium text-accent uppercase">
          {box.markerType === 'aruco' ? `#${box.markerId}` : 'QR'}
        </span>
      </div>

      {/* Location */}
      {box.location && (
        <div className="mt-2 flex items-center gap-1">
          <svg
            className="h-3.5 w-3.5 text-text-secondary"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <span className="text-xs text-text-secondary truncate">{box.location}</span>
        </div>
      )}

      {/* Footer: date */}
      <p className="mt-2 text-[11px] text-text-secondary">
        {new Date(box.createdAt).toLocaleDateString()}
      </p>
    </button>
  );
}
